import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import {
  useEntryRules,
  useCreateEntryRule,
  useUpdateEntryRule,
  useSetMakerFlag,
  useManualConfirmedPairs
} from '../api/useEntry';
import { useEntrySocket } from '../hooks/useEntrySocket';
import {
  EntryTabSumOfSides,
  CreateEntrySumRuleParams
} from '../components/entry/EntryTabSumOfSides';
import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  DEFAULT_TARGET_TNA_PERCENT,
  MakerFlag
} from '../types/entry.types';

// Each per-(platform, side) maker flag uniquely identifies the entry
// path + the leg that should rest as maker. EntryPage maps the flag
// to the (kalshiConfig.side, polymarketConfig.side) combo it persists
// onto a freshly-created rule so the engine's existing side-driven
// code keeps working.
const FLAG_TO_SIDES: Record<
  MakerFlag,
  { polySide: 'yes' | 'no'; kalshiSide: 'yes' | 'no' }
> = {
  polyYesMaker: { polySide: 'yes', kalshiSide: 'no' },
  kalshiNoMaker: { polySide: 'yes', kalshiSide: 'no' },
  polyNoMaker: { polySide: 'no', kalshiSide: 'yes' },
  kalshiYesMaker: { polySide: 'no', kalshiSide: 'yes' },
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export const EntryPage = () => {
  const { data: pairsData, isLoading } = useManualConfirmedPairs();
  const { data: rules } = useEntryRules();
  const createRuleMutation = useCreateEntryRule();
  const updateRuleMutation = useUpdateEntryRule();
  const setMakerFlagMutation = useSetMakerFlag();
  const { isConnected, getState } = useEntrySocket();

  // Sum of Sides is the operator-facing default (mirrors UnwindingPage).
  const [tab, setTab] = useState<'tna' | 'sum_of_sides'>('sum_of_sides');

  const pairs: ManualConfirmedPair[] = useMemo(() => pairsData ?? [], [pairsData]);

  const globalEnabled = Array.isArray(rules) && rules.some(r => r.status === 'active');

  // Per-tab rule slices. Legacy docs without ruleType default to 'tna'.
  const sumRules = useMemo(
    () => (rules ?? []).filter(r => r.ruleType === 'sum_of_sides'),
    [rules]
  );

  // All rules keyed by pairId — used by the row's status indicator so it
  // works regardless of the active tab's rule type.
  const anyRulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      const existing = map.get(rule.pairId);
      if (!existing) {
        map.set(rule.pairId, rule);
        continue;
      }
      const incomingActive = rule.status === 'active';
      const existingActive = existing.status === 'active';
      if (incomingActive && !existingActive) {
        map.set(rule.pairId, rule);
      }
    }
    return map;
  }, [rules]);

  const handleCreateSumRule = useCallback(
    ({ pair, makerFlag, thresholdSum, size, maxVolumeUsd }: CreateEntrySumRuleParams) => {
      if (!pair._id) return;
      // Hard-cap layer 3: never POST a rule with thresholdSum >= 0.995.
      if (!(thresholdSum > 0 && thresholdSum < ENTRY_HARD_CAP_GROSS)) {
        // Defensive — the row already validates, but a stale state could
        // slip through.
        return;
      }
      // Derive sides from the chosen maker flag — the backend re-runs
      // the same derivation in RulesService, so the values here just
      // give it a head start (and let older callers without flag
      // awareness still produce a valid rule).
      const { polySide, kalshiSide } = FLAG_TO_SIDES[makerFlag];
      createRuleMutation.mutate({
        pairId: pair._id,
        pairName: pair.pairName,
        expiryDate: pair.expiryDate || new Date().toISOString(),
        ruleType: 'sum_of_sides',
        thresholdSum,
        kalshiConfig: pair.kalshi?.ticker
          ? {
              side: kalshiSide,
              ticker: pair.kalshi.ticker,
              contractsToBuy: size,
              targetTnaPercent: DEFAULT_TARGET_TNA_PERCENT
            }
          : undefined,
        polymarketConfig: pair.polymarket?.tokenId
          ? {
              side: polySide,
              tokenId: pair.polymarket.tokenId,
              conditionId: pair.polymarket.conditionId ?? pair.polymarket.tokenId,
              contractsToBuy: size,
              targetTnaPercent: DEFAULT_TARGET_TNA_PERCENT
            }
          : undefined,
        maxVolumeUsd,
        volumePeriod: 'day',
        // Engine-side platform enables stay defaulted to true; the
        // 4 maker flags are the operator-facing controls.
        kalshiEnabled: true,
        polymarketEnabled: true,
        [makerFlag]: true,
      });
    },
    [createRuleMutation]
  );

  const handleSetMakerFlag = useCallback(
    async (
      rule: EntryRule,
      flag: MakerFlag,
      value: boolean,
      clearOthers?: MakerFlag[],
    ) => {
      await setMakerFlagMutation.mutateAsync({
        id: rule._id,
        flag,
        value,
        clearOthers,
      });
    },
    [setMakerFlagMutation],
  );

  const handleUpdateRule = useCallback(
    async (rule: EntryRule, dto: UpdateEntryRuleDto) => {
      try {
        await updateRuleMutation.mutateAsync({ id: rule._id, dto });
        return true;
      } catch {
        return false;
      }
    },
    [updateRuleMutation]
  );

  return (
    <Box sx={{ px: 2, pt: 1, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Title bar */}
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Opening
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: '0.6rem !important' }} />}
            label={isConnected ? 'Live' : 'Disconnected'}
            size="small"
            color={isConnected ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <FormControlLabel
            control={<Switch size="small" checked={globalEnabled} disabled />}
            label="Global"
            sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' }, mr: 0 }}
          />
        </Stack>
      </Stack>

      {/* Tab selector */}
      <Tabs
        value={tab}
        onChange={(_, v: 'tna' | 'sum_of_sides') => setTab(v)}
        sx={{
          minHeight: 32,
          mb: 0.5,
          flexShrink: 0,
          '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.75rem' }
        }}
      >
        <Tab value="tna" label="TNA" />
        <Tab value="sum_of_sides" label="Sum of Sides" />
      </Tabs>

      <Box sx={{ flex: 3, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !isConnected ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
              Disconnected — waiting for opener backend.
            </Typography>
          </Box>
        ) : tab === 'sum_of_sides' ? (
          <EntryTabSumOfSides
            pairs={pairs}
            rules={sumRules}
            anyRulesByPairId={anyRulesByPairId}
            getState={getState}
            onCreateRule={handleCreateSumRule}
            onSetMakerFlag={handleSetMakerFlag}
            onUpdateRule={handleUpdateRule}
          />
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
              TNA-based entry is not yet implemented. Use the Sum of Sides
              tab.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
